import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterBaseOption, FilterOptionGroup } from '../types';

type UseFilterReturnType = {
  filteredOptions?: MenuItemProps[];
  handleToggleOption: (option: FilterBaseOption) => void;
};

type UseFilterArgs = {
  optionGroups?: FilterOptionGroup[];
  options?: FilterBaseOption[];
  selectedOptions: FilterBaseOption[];
  onValueChange: (options: FilterBaseOption[]) => void;
  isGrouped?: boolean;
  renderOption?: (option: FilterBaseOption) => JSX.Element;
};

export const useFilter = ({
  optionGroups,
  options,
  selectedOptions,
  onValueChange,
  isGrouped,
  renderOption,
}: UseFilterArgs): UseFilterReturnType => {
  const [selectedOptionValues, setSelectedOptionValues] = useState<Set<string>>(new Set());

  const handleToggleOption = useCallback(
    (option: FilterBaseOption): void => {
      const existingIndex = selectedOptions.findIndex(({ value }: FilterBaseOption) => value === option.value);
      const newValue = selectedOptions.slice();
      if (existingIndex > -1) {
        newValue.splice(existingIndex, 1);
      } else {
        newValue.push(option);
      }
      onValueChange(newValue);
    },
    [selectedOptions, onValueChange],
  );

  const filteredOptions: MenuItemProps[] = useMemo(() => {
    if (isGrouped && optionGroups) {
      return optionGroups.reduce((groupList, group) => {
        const elementsInGroup: MenuItemProps[] = group.options.map((option) => ({
          groupLabel: group.groupName,
          label: option.label,
          value: option.value,
          children: renderOption ? renderOption(option) : undefined,
          isSelected: selectedOptionValues.has(option.value),
          onSelect: (): void => {
            handleToggleOption(option);
          },
        }));
        return [...groupList, ...elementsInGroup];
      }, [] as MenuItemProps[]);
    }

    if (options) {
      return options.map((option) => {
        const item: MenuItemProps = {
          label: option.label,
          value: option.value,
          onSelect: (): void => {
            handleToggleOption(option);
          },
          children: renderOption ? renderOption(option) : undefined,
        };
        return item;
      });
    }

    return [];
  }, [handleToggleOption, isGrouped, optionGroups, options, renderOption, selectedOptionValues]);

  // Fixes an issue where the selectedOptions are not updated in time under this condition
  // Open the filter, click the "Clear All Filters" button and then re-open the filter
  useEffect(() => {
    setTimeout(() => {
      setSelectedOptionValues(new Set(selectedOptions.map(({ value }) => value)));
    }, 200);
  }, [selectedOptions]);

  return {
    filteredOptions,
    handleToggleOption,
  };
};
